import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import { formatSizeLabel } from '../designSizes';
import { notifyTemplateSaved } from '../templateHoverTitles';
import { apiBase, withApiKey } from '../apiAuth';
import {
  getSessionTemplatePassword,
  setSessionTemplatePassword,
} from '../templatePassword';

type Mode = 'create' | 'overwrite';

type CatalogItem = {
  uniqueId: string;
  title?: string;
  preview?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  store: RaeditorStore;
  capturePreview: (store: RaeditorStore) => Promise<string>;
};

function readStoreSize(store: RaeditorStore): { width: number; height: number; pages: number } {
  const pages = (store.pages || []) as Array<{
    computedWidth?: number;
    computedHeight?: number;
    width?: number | string;
    height?: number | string;
  }>;
  const page = pages[0];
  const width =
    (typeof page?.computedWidth === 'number' && page.computedWidth) ||
    (typeof page?.width === 'number' && page.width) ||
    (typeof store.width === 'number' && store.width) ||
    0;
  const height =
    (typeof page?.computedHeight === 'number' && page.computedHeight) ||
    (typeof page?.height === 'number' && page.height) ||
    (typeof store.height === 'number' && store.height) ||
    0;
  return { width, height, pages: pages.length };
}

export function SaveTemplateModal({ open, onClose, onStatus, store, capturePreview }: Props) {
  const titleId = useId();
  const [title, setTitle] = useState('My template');
  const [mode, setMode] = useState<Mode>('create');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [password, setPassword] = useState(() => getSessionTemplatePassword());

  const sizeInfo = useMemo(() => (open ? readStoreSize(store) : null), [open, store, store.pages, store.width, store.height]);

  const loadUserTemplates = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/get-templates?page=1`));
      const data = (await res.json().catch(() => ({}))) as {
        items?: CatalogItem[];
        error?: string;
      };
      if (!res.ok) {
        setError(data.error || `Failed to load templates (${res.status})`);
        setItems([]);
        return;
      }
      const userOnly = (data.items || []).filter((it) =>
        String(it.uniqueId || '').startsWith('user-'),
      );
      setItems(userOnly);
      setSelectedId((prev) => {
        if (prev && userOnly.some((u) => u.uniqueId === prev)) return prev;
        return userOnly[0]?.uniqueId || '';
      });
    } catch (err) {
      console.error(err);
      setError('Could not reach /api/get-templates');
      setItems([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTitle('My template');
    setMode('create');
    setConfirmOverwrite(false);
    setPassword(getSessionTemplatePassword());
    setError(null);
    setSaving(false);
    void loadUserTemplates();
  }, [open, loadUserTemplates]);

  if (!open) return null;

  const selected = items.find((i) => i.uniqueId === selectedId);
  const canSubmit =
    !saving &&
    title.trim().length > 0 &&
    password.trim().length > 0 &&
    (mode === 'create' || (mode === 'overwrite' && !!selectedId && confirmOverwrite));

  const handleSubmit = async () => {
    const trimmed = title.trim() || 'My template';
    if (mode === 'overwrite' && !confirmOverwrite) {
      setError('Please confirm overwrite before saving.');
      return;
    }
    const pass = password.trim();
    if (!pass) {
      setError('Password required to save templates.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onStatus(mode === 'overwrite' ? 'Overwriting template…' : 'Saving template…');
      const previewDataUrl = await capturePreview(store);
      const body = {
        title: trimmed,
        json: store.toJSON(),
        previewDataUrl,
        password: pass,
      };
      const headers = {
        'Content-Type': 'application/json',
        'X-Template-Password': pass,
        'X-Delete-Password': pass,
      };

      let res: Response;
      if (mode === 'overwrite') {
        res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(selectedId)}`), {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(withApiKey(`${apiBase()}/api/templates`), {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
      }

      const data = (await res.json().catch(() => ({}))) as {
        uniqueId?: string;
        error?: string;
        message?: string;
        preview?: string;
        updatedAt?: string;
      };
      if (!res.ok) {
        const msg = data.error || data.message || String(res.status);
        setError(msg);
        onStatus(`Save as template failed: ${msg}`);
        return;
      }

      const id = data.uniqueId || selectedId || 'ok';
      const sizeBit = sizeInfo ? ` · ${formatSizeLabel(sizeInfo.width, sizeInfo.height)}` : '';
      // Bust browser cache on template panel thumbnails + refresh titles
      notifyTemplateSaved({
        uniqueId: id,
        preview: data.preview,
        updatedAt: data.updatedAt,
      });
      setSessionTemplatePassword(pass);
      if (mode === 'overwrite') {
        onStatus(`Template overwritten “${trimmed}” (${id})${sizeBit}`);
      } else {
        onStatus(`Template created “${trimmed}” (${id})${sizeBit}`);
      }
      onClose();
    } catch (err) {
      console.error(err);
      setError('Save failed — is the API running?');
      onStatus('Save as template failed — is the API running?');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            Save as template
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Save the current design to the Worker catalog. Create a new user template or overwrite an
          existing one.
        </p>

        {sizeInfo && (
          <div className="modal__size-badge" role="status">
            Canvas size:{' '}
            <strong>{formatSizeLabel(sizeInfo.width, sizeInfo.height)}</strong>
            {sizeInfo.pages > 1 ? ` · ${sizeInfo.pages} pages` : ''}
          </div>
        )}

        <label className="modal__label" htmlFor="save-tmpl-title">
          Title
        </label>
        <input
          id="save-tmpl-title"
          className="modal__input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My template"
          autoFocus
        />

        <fieldset className="modal__fieldset">
          <legend className="modal__label">Save mode</legend>
          <label className="modal__radio">
            <input
              type="radio"
              name="save-tmpl-mode"
              checked={mode === 'create'}
              onChange={() => {
                setMode('create');
                setConfirmOverwrite(false);
              }}
            />
            <span>Create new</span>
          </label>
          <label className="modal__radio">
            <input
              type="radio"
              name="save-tmpl-mode"
              checked={mode === 'overwrite'}
              onChange={() => setMode('overwrite')}
            />
            <span>Overwrite existing</span>
          </label>
        </fieldset>

        {mode === 'overwrite' && (
          <div className="modal__overwrite">
            <label className="modal__label" htmlFor="save-tmpl-existing">
              User template
            </label>
            {loadingList ? (
              <div className="modal__status">Loading user templates…</div>
            ) : items.length === 0 ? (
              <div className="modal__status">No user templates yet. Create new instead.</div>
            ) : (
              <select
                id="save-tmpl-existing"
                className="modal__input"
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setConfirmOverwrite(false);
                  const match = items.find((i) => i.uniqueId === e.target.value);
                  if (match?.title) setTitle(match.title);
                }}
              >
                {items.map((it) => (
                  <option key={it.uniqueId} value={it.uniqueId}>
                    {it.title || 'Untitled'} ({it.uniqueId})
                  </option>
                ))}
              </select>
            )}

            {selected && (
              <label className="modal__warn-check">
                <input
                  type="checkbox"
                  checked={confirmOverwrite}
                  onChange={(e) => setConfirmOverwrite(e.target.checked)}
                />
                <span>
                  I understand this will permanently replace “{selected.title || selected.uniqueId}
                  ” (JSON + preview).
                </span>
              </label>
            )}
          </div>
        )}

        <label className="modal__label" htmlFor="save-tmpl-password">
          Password
        </label>
        <input
          id="save-tmpl-password"
          className="modal__input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Template save password"
        />
        <p className="modal__hint" style={{ marginTop: 4, fontSize: 12, opacity: 0.75 }}>
          Same password as delete. Remembered for this browser tab until you close it.
        </p>

        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--accent"
            disabled={!canSubmit || (mode === 'overwrite' && items.length === 0)}
            onClick={() => void handleSubmit()}
          >
            {saving ? 'Saving…' : mode === 'overwrite' ? 'Overwrite template' : 'Create template'}
          </button>
        </div>
      </div>
    </div>
  );
}
